/**
 * Customer display (second monitor) — the whole transport surface in one place.
 *
 * The two windows talk to each other DIRECTLY over a BroadcastChannel, not
 * through the shell. Both load this same React build on one origin in one
 * session — `rista-app://rista-billing/{index.html,customer-display}` under
 * Electron, the `http://localhost:3000` iframes of index.html/customer.html
 * under NW.js — so the channel reaches across without any native help. That is
 * what lets one implementation serve both shells, and lets the display be
 * developed in two ordinary browser tabs.
 *
 * The shell is still needed for the one thing a renderer cannot do: place and
 * tear down a window on the non-primary monitor. That, and only that, keeps a
 * platform branch below.
 *
 * Uplifted from `apps/web/src/utils/customerDisplay.ts` because the sale-update middleware that
 * pushes the cart now lives in core. WEB-ONLY, so a single `.desktop` variant: `ls apps/mobile/src/utils`
 * has no counterpart, and mobile needs none — it is one process, and its display screen reads the
 * same store the POS screens write to (see apps/mobile/src/screens/CustomerDisplay).
 *
 * ⚠️ Moved WHOLE, not seeded with just `pushCustomerCart`. The module-scoped `channel` and
 * `lastPayload` are shared state: splitting the file would give the app's `answerCartRequests` a
 * different `lastPayload` than core's `pushCustomerCart` writes, and replay would answer with the
 * wrong cart. The app file re-exports every symbol, so its three remaining importers are unchanged.
 */
import { CustomerCartPayload } from "../../customerDisplay";

import { exec, isElectron } from "../nativeHooks";
import { NW_APP_EVENT_KEYS, NW_APP_EVENT_ACTIONS } from "../../data/constants";
import { CUSTOMER_DISPLAY_SCREEN } from "../routeConstants";

/** What the display hands back to the POS window. */
export type DisplayMessage = { type: "customerNumber"; number: string };

type Unsubscribe = () => void;

type Wire =
  | { kind: "cart"; payload: CustomerCartPayload }
  | { kind: "cart-request" }
  | { kind: "to-pos"; message: DisplayMessage };

const CHANNEL = "rista-customer-display";

/** True in the display window itself — it mounts a different React tree. */
export const isCustomerDisplay = () =>
  window.location.pathname.endsWith(CUSTOMER_DISPLAY_SCREEN);

// ── Channel ───────────────────────────────────────────────────────────────────
// One channel object per window, opened on first use. A BroadcastChannel never
// delivers to the object that sent the message, so POS and display can share
// this module without hearing themselves.

let channel: BroadcastChannel | null = null;

const bus = () => {
  if (!channel) channel = new BroadcastChannel(CHANNEL);
  return channel;
};

const send = (message: Wire) => bus().postMessage(message);

const listen = (kind: Wire["kind"], cb: (message: Wire) => void): Unsubscribe => {
  const handler = (event: MessageEvent<Wire>) => {
    if (event.data?.kind === kind) cb(event.data);
  };
  bus().addEventListener("message", handler);
  return () => bus().removeEventListener("message", handler);
};

// ── Window lifecycle (the only platform branch) ───────────────────────────────
// Gated on the method, not just on isElectron(): React ships over OTA
// independently of the shell, so this bundle can run on a shell predating the
// named channels. There the method is absent and we fall back to exec(), which
// that shell still answers on 'dualscreen:exec'.

const nativeApi = (method: string) => {
  if (!isElectron()) return undefined;
  const api = (window as any).electronAPI?.customerDisplay;
  return typeof api?.[method] === "function" ? api : undefined;
};

/** POS window: claim the second monitor. Resolves { success } or { error }. */
export const openCustomerDisplay = () => {
  const api = nativeApi("open");
  if (api) return api.open();
  exec(NW_APP_EVENT_KEYS.dualscreen, NW_APP_EVENT_ACTIONS.dualscreen.open, []);
  return Promise.resolve({ success: "OK" });
};

/** POS window: tear the display down. */
export const closeCustomerDisplay = () => {
  const api = nativeApi("close");
  if (api) return api.close();
  exec(NW_APP_EVENT_KEYS.dualscreen, NW_APP_EVENT_ACTIONS.dualscreen.close, []);
  return Promise.resolve({ success: "OK" });
};

// ── Cart, POS → display ───────────────────────────────────────────────────────

// Last payload wins — the display only ever renders current cart state, so
// there is nothing to queue. Held in the POS window (it used to live in the
// shell) purely to answer a replay request.
let lastPayload: CustomerCartPayload | null = null;

/**
 * POS window: mirror the current cart onto the display.
 *
 * The payload crosses a structured clone, so it must stay free of functions —
 * one throws and takes the whole push down with it.
 */
export const pushCustomerCart = (payload: CustomerCartPayload) => {
  lastPayload = payload;
  send({ kind: "cart", payload });
};

/** Display window: subscribes to cart pushes. Returns an unsubscribe function. */
export const onCustomerCart = (
  cb: (payload: CustomerCartPayload) => void,
): Unsubscribe =>
  listen("cart", (message) => cb((message as { payload: CustomerCartPayload }).payload));

/**
 * Display window: ask for a replay of the current cart after mounting, so a
 * display that started (or reloaded) mid-order is not blank until the next
 * cart edit.
 */
export const requestCustomerCart = () => send({ kind: "cart-request" });

/**
 * POS window: answer those replay requests. Returns an unsubscribe function.
 * Silent until the first cart push — an empty display is the correct render
 * for an empty cart.
 */
export const answerCartRequests = (): Unsubscribe =>
  listen("cart-request", () => {
    if (lastPayload) send({ kind: "cart", payload: lastPayload });
  });

// ── Messages, display → POS ───────────────────────────────────────────────────

/**
 * Display window → POS window. The display has no store and no auth token, so
 * anything needing either is asked of the POS instead.
 */
export const sendToPos = (message: DisplayMessage) => send({ kind: "to-pos", message });

/** POS window: receives what the display sent. Returns an unsubscribe function. */
export const onDisplayMessage = (
  cb: (message: DisplayMessage) => void,
): Unsubscribe =>
  listen("to-pos", (wire) => cb((wire as { message: DisplayMessage }).message));
